import React from "react"
import {
  FormGroupWrapper,
  Heading2,
  Heading3,
  Heading4,
  ListItem,
  PageContainer,
  PageSection,
  ParagraphText,
  UnorderedList,
} from "../../UIKit/styles"
import {
  FormInput,
  InfoBox,
  RadioGroup,
  SelectInput,
  TableInput,
} from "../../UIKit/components"
import {
  getFieldIsInvalid,
  getSavedFieldValue,
  getValidationMessage,
  getFieldsToSet
} from "../../utils/utils"

export default function Calculator({
  title,
  info,
  description,
  sections = [],
  setData,
  data = {},
}) {
  //add the new value to the data object and return
  const getUpdatedDataForSection = (sectionData, subsection, field, value) => {
    if (subsection) {
      sectionData[subsection] = {
        ...(sectionData[subsection] || {}),
        [field]: value,
      }
    } else {
      sectionData[field] = value
    }
    return sectionData
  }

  //handle the updates for the value and also any associated total calculations
  const handleOnChangeValue = (value, field, sectionName, subsection) => {
    const sectionData = data[sectionName] || {}
    let updatedData = {
      ...data,
      [sectionName]: getUpdatedDataForSection(
        sectionData,
        subsection,
        field,
        value
      ),
    }
    const sectionsWithTotals = sections.filter((section) => section.totalCalc)
    if (sectionsWithTotals.length) {
      //for each section that requires the total to be calculated, calculate the total from all the number fields in that section, and save to state and session storage
      sectionsWithTotals.forEach((sectionInfo) => {
        const { totalCalc, inputFields } = sectionInfo
        const total = inputFields.reduce((result, inputField) => {
          const { section, field, subsection, type } = inputField
          const value = getSavedFieldValue(section, subsection, field, data)
          if (type !== "number" || !value) return result
          return result + Number(value)
        }, 0)
        updatedData[totalCalc.section] = getUpdatedDataForSection(
          updatedData[totalCalc.section] || {},
          totalCalc.subsection,
          totalCalc.field,
          total
        )
      })
    }
    if (
      field === "percentageOfOrchardIsSteel" ||
      field === "percentageOfOrchardIsWood"
    ) {
      const oppositeField =
        field === "percentageOfOrchardIsSteel"
          ? "percentageOfOrchardIsWood"
          : "percentageOfOrchardIsSteel"
      data[sectionName][oppositeField] = 100 - value
    }

    let fieldsToSet = getFieldsToSet(field, value, sections);

    if(fieldsToSet && fieldsToSet.length > 0) {
      fieldsToSet.forEach((fieldToSet) => {
        const { field, value } = fieldToSet;
        updatedData = {
          ...data,
          [sectionName]: getUpdatedDataForSection(
            sectionData,
            subsection,
            field,
            value
          ),
        }
      });
    }

    setData(updatedData)
  }

  const getRows = (columns, rows, section) => {
    return rows.map((row) => ({
      ...row,
      values: columns.reduce((result, column) => {
        return {
          ...result,
          [column.subsection]: getSavedFieldValue(
            section,
            column.subsection,
            row.field,
            data
          ),
        }
      }, {}),
    }))
  }

  const getIsReadOnly = (field, readOnly = false) => {
    if(readOnly === true) return true;
    if(!data) return false;

    if(readOnly === "onSave") {
      const url = new URL(window.location.href);
      const shareId = url.searchParams.get("shareId");

      return( (data[field] !== undefined || (data.calculatorDetails && data.calculatorDetails[field]))) && (data.shareId !== undefined || shareId !== null);
    }

    return false;

  }

  const getFieldInput = ({
    field,
    section,
    subsection,
    type = "text",
    options,
    rows,
    columns,
    title,
    message,
    helpText,
    initialValue,
    readOnly,
    ...otherProps
  }) => {
    const value = getSavedFieldValue(section, subsection, field, data, initialValue)
    let filteredColumns = columns && columns.length ? columns.filter((column) => {
      const variety = column.subsection;
      const varietyExists = getSavedFieldValue('varieties', variety, "productiveCanopyArea", data)
      return varietyExists > 0;
    }) : columns;

    switch (type) {
      case "table": {
        return (
          <TableInput
            {...otherProps}
            key={`table-${otherProps.label || title}`}
            label={otherProps.label}
            id={otherProps.label || title}
            columns={filteredColumns}
            rows={getRows(columns, rows, section)}
            onChange={(field, subsection, value) =>
              handleOnChangeValue(value, field, section, subsection)
            }
          />
        )
      }
      case "radio": {
        return (
          <RadioGroup
            key={`${section}-${field}-${subsection}`}
            {...otherProps}
            id={field}
            onChange={(value) =>
              handleOnChangeValue(value, field, section, subsection)
            }
            value={value}
            options={options.map((option) => ({
              ...option,
              subcontent: getSubcontent(option.subFields)
            }))}
            aria-invalid={getFieldIsInvalid(value, 'radio', {...otherProps })}
            validationText={getValidationMessage(value, 'radio', {...otherProps})}
          />
        )
      }
      case "select": {
        return (
          <SelectInput
            key={`${section}-${field}-${subsection}`}
            {...otherProps}
            id={field}
            options={options}
            onChange={(value) =>
              handleOnChangeValue(value, field, section, subsection)
            }
            value={value}
            aria-invalid={getFieldIsInvalid(value, type, {...otherProps})}
            helpText={helpText}
            validationText={getValidationMessage(value, type, {...otherProps})}
          />
        )
      }
      case "message": {
        return (
          <p
            key={`${section}-${field}-${subsection}`}
            id={field}
            dangerouslySetInnerHTML={{ __html: message }}
          />
        );
      }
      default: {
        return (
          <FormInput
            key={`${section}-${field}-${subsection}`}
            {...otherProps}
            type={type}
            id={field}
            onChange={(value) =>
              handleOnChangeValue(value, field, section, subsection)
            }
            value={value}
            aria-invalid={getFieldIsInvalid(value, type, {...otherProps})}
            helpText={helpText}
            validationText={getValidationMessage(value, type, {...otherProps})}
            readOnly={getIsReadOnly(field, readOnly)}
          />
        )
      }
    }
  }

  const getSubcontent = (subFields) => {
    if (!subFields) return null
    return <>{subFields.map((subField) => getFieldInput(subField))}</>
  }

  const getTotalDisplay = ({ totalCalc }) => {
    if (!totalCalc) return null
    const { section, subsection, field, unit, label } = totalCalc
    const value = getSavedFieldValue(section, subsection, field, data) || 0
    const displayText = `${label}: ${value.toLocaleString()}${
      unit ? ` ${unit}` : ""
    }`
    return <ParagraphText>{displayText}</ParagraphText>
  }

  return (
    <PageContainer>
      <Heading2>{title}</Heading2>
      {description && (
        <PageSection>
          <ParagraphText dangerouslySetInnerHTML={{ __html: description }}></ParagraphText>
        </PageSection>
      )}
      {info && (
        <PageSection>
          <InfoBox>
            {info.map(({ type, value }, i) =>
              type === "list" ? (
                <UnorderedList key={`info-${i}`}>
                  {value.map((text) => (
                    <ListItem key={text} dangerouslySetInnerHTML={{ __html: text }}></ListItem>
                  ))}
                </UnorderedList>
              ) : (
                <ParagraphText key={`info-${i}`}  dangerouslySetInnerHTML={{ __html: value }}></ParagraphText>
              )
            )}
          </InfoBox>
        </PageSection>
      )}
      {sections.map((section) => (
        <PageSection key={section.title}>
          <Heading3>{section.title}</Heading3>
          <Heading4>{section.subTitle}</Heading4>
          {section.info && (
            <InfoBox>
              <ParagraphText>{section.info}</ParagraphText>
            </InfoBox>
          )}
          <FormGroupWrapper>
            {section.inputFields.map((inputField) =>
              getFieldInput({ ...inputField, title: section.title })
            )}
            {getTotalDisplay(section)}
          </FormGroupWrapper>
        </PageSection>
      ))}
    </PageContainer>
  )
}
